import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CollapsibleSection } from "../CollapsibleSection";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";

interface DevotionalNotesProps {
  userId?: string;
  devotionalId: string;
}

export const DevotionalNotes = ({ userId, devotionalId }: DevotionalNotesProps) => {
  const [notes, setNotes] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [devotionalExists, setDevotionalExists] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const checkDevotional = async () => {
      if (devotionalId) {
        const { data, error } = await supabase
          .from("devotionals")
          .select("date")
          .eq("date", devotionalId)
          .maybeSingle();

        if (error) {
          console.error("Error checking devotional:", error);
          return;
        }

        setDevotionalExists(!!data);
      }
    };

    checkDevotional();
  }, [devotionalId]);

  useEffect(() => {
    if (userId && devotionalId && devotionalExists) {
      const fetchNotes = async () => {
        console.log("Fetching notes for user:", userId, "devotional:", devotionalId);
        const { data, error } = await supabase
          .from("user_notes")
          .select("content")
          .eq("user_id", userId)
          .eq("devotional_id", devotionalId)
          .maybeSingle();

        if (error) {
          console.error("Error fetching notes:", error);
          return;
        }

        if (data) {
          setNotes(data.content);
        }
      };

      fetchNotes();
    }
  }, [userId, devotionalId, devotionalExists]);

  const handleSaveNotes = async () => {
    if (!userId) {
      toast({
        title: "Login Required",
        description: "Please sign in to save your notes",
        action: (
          <Button variant="outline" onClick={() => navigate("/login")}>
            Sign In
          </Button>
        ),
      });
      return;
    }

    if (!devotionalExists) {
      toast({
        title: "Cannot save notes",
        description: "This devotional is not yet available",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    console.log("Saving notes for user:", userId, "devotional:", devotionalId);
    
    try {
      const { data: existingNote, error: fetchError } = await supabase
        .from("user_notes")
        .select("id")
        .eq("user_id", userId)
        .eq("devotional_id", devotionalId)
        .maybeSingle();

      if (fetchError) {
        throw fetchError;
      }

      let error;
      if (existingNote) {
        const { error: updateError } = await supabase
          .from("user_notes")
          .update({
            content: notes,
            updated_at: new Date().toISOString(),
          })
          .eq("id", existingNote.id);
        error = updateError;
      } else {
        const { error: insertError } = await supabase
          .from("user_notes")
          .insert({
            user_id: userId,
            devotional_id: devotionalId,
            content: notes,
            updated_at: new Date().toISOString(),
          });
        error = insertError;
      }

      if (error) {
        throw error;
      }

      toast({
        title: "Notes saved",
        description: "Your notes have been saved successfully",
      });
    } catch (error) {
      console.error("Error saving notes:", error);
      toast({
        title: "Error saving notes",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <CollapsibleSection title="Notes" id="notes">
      <div className="space-y-4">
        <textarea
          className="w-full min-h-[100px] p-4 border rounded-lg focus:outline-none focus:ring-2 focus:ring-sage-500 text-lg"
          placeholder={userId ? "Write your personal notes and reflections here..." : "Sign in to save your notes"}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={!devotionalExists}
        />
        {userId && (
          <div className="flex justify-end">
            <Button
              onClick={handleSaveNotes}
              disabled={isSaving || !devotionalExists}
              className="bg-sage-600 hover:bg-sage-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSaving ? "Saving..." : "Save Notes"}
            </Button>
          </div>
        )}
      </div>
    </CollapsibleSection>
  );
};